<template>
  <page>
    <div :load="doInit">
      <v-expansion-panels v-model="panel1" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div><v-icon>mdi-filter-variant</v-icon> Date Range</div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card>
              <v-card-text>
                <v-form>
                  <v-container fluid grid-list-sm>
                    <v-layout row wrap>
                      <v-flex xs6>
                        <cal-picker
                          v-model="reportForm.tranDateFrom"
                          :label="labels.tranDateFrom"
                          v-validate="fromDateValidation(reportForm.tranDateTo)"
                          :data-vv-as="labels.tranDateFrom"
                          data-vv-name="tranDateFrom"
                          :error-messages="errors.collect('tranDateFrom')"
                        ></cal-picker>
                      </v-flex>
                      <v-flex xs6>
                        <cal-picker
                          v-model="reportForm.tranDateTo"
                          :label="labels.tranDateTo"
                          v-validate="toDateValidation(reportForm.tranDateFrom)"
                          :data-vv-as="labels.tranDateTo"
                          data-vv-name="tranDateTo"
                          :error-messages="errors.collect('tranDateTo')"
                          :min="reportForm.tranFromData"
                          :max="today"
                        ></cal-picker>
                      </v-flex>
                    </v-layout>
                  </v-container>
                </v-form>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-spacer class="mt-2"></v-spacer>
      <v-expansion-panels v-model="panel2" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div><v-icon>mdi-filter-variant</v-icon> Selection Criteria</div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card>
              <v-card-text>
                <v-container fluid grid-list-sm>
                  <v-layout row wrap>
                    <!-- Department(s) -->
                    <v-flex xs12>
                      <v-text-field
                        v-model="reportForm.dept"
                        :label="labels.dept"
                        :error-messages="errors.collect('dept')"
                        :data-vv-as="labels.dept"
                        data-vv-name="dept"
                        append-icon="mdi-magnify"
                        @click:append="multiDeptHelpList = true"
                        readonly
                      ></v-text-field>
                    </v-flex>
                    <!-- Policy Type(s) -->
                    <v-flex xs12>
                      <v-text-field
                        v-model="reportForm.polType"
                        :label="labels.polType"
                        :error-messages="errors.collect('polType')"
                        :data-vv-as="labels.polType"
                        data-vv-name="polType"
                        append-icon="mdi-magnify"
                        @click:append="multiPolTypeHelpList = true"
                        readonly
                      ></v-text-field>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-spacer class="mt-2"></v-spacer>
      <v-expansion-panels v-model="panel3" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div><v-icon>mdi-sort</v-icon> Sort Order(s)</div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Page Header Sorting Order HelpList -->
            <sort-order-custom-help-list
              ref="pageHeaderSortRef"
              :title="pageHeaderSorting"
              :orderIdList="orderIdList"
              :showOrderError="pageHeaderSortError"
              :selectedSize="selectedSize"
            ></sort-order-custom-help-list>
            <!-- Detail Line Sorting Order HelpList -->
            <sort-order-custom-help-list
              ref="detailLineSortRef"
              :title="detailLineSorting"
              :orderIdList="orderIdList"
              :showOrderError="detailLineSortError"
              :selectedSize="selectedSize"
            ></sort-order-custom-help-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-spacer class="mt-2"></v-spacer>
      <v-form>
        <v-flex xs12>
              <v-checkbox
                      :label="labels.pbPrtParam"
                      v-model="reportForm.pbPrtParam"
                      :error-messages="errors.collect('pbPrtParam')"
                      data-vv-name="pbPrtParam"
                      :data-vv-as="labels.pbPrtParam"
                    />
                  </v-flex>
        <v-container fluid grid-list-sm>
          <v-layout row wrap>
            <v-flex lg6 xs12>
              <v-btn class="mr-2" color="primary" type="submit" :loading="loading" @click.prevent="doExport">
                <v-icon left dark>mdi-file-export</v-icon>
                {{ labels.export }}
              </v-btn>
              <v-btn-toggle dense mandatory v-model="reportForm.format">
                <v-btn text v-for="format in supportedList" :key="format.value" :value="format.value">
                  <span>{{ format.label }}</span>
                  <v-icon :color="format.color">{{ format.icon }}</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-flex>
          </v-layout>
        </v-container>
      </v-form>
    </div>
    <!-- Department Helplist -->
    <v-dialog v-model="multiDeptHelpList" width="900px" persistent>
      <multi-dept-help-list :dept="reportForm.dept" @close="multiDeptHelpList = false" @doAssign="doAssign('dept', $event)"></multi-dept-help-list>
    </v-dialog>
    <!-- Policy Type Helplist -->
    <v-dialog v-model="multiPolTypeHelpList" width="900px" persistent>
      <multi-pol-type-help-list
        :pt="reportForm.polType"
        @close="multiPolTypeHelpList = false"
        @doAssign="doAssign('polType', $event)"
      ></multi-pol-type-help-list>
    </v-dialog>
  </page>
</template>

<script>
import { extractMenu } from '@/utils/extract'
import { reportFormat } from '@/const/report'
import MessageMixin from '@/mixins/message'
import { commonAjaxMixin } from '@/mixins/common/commonAjaxMixin'
import { checkDateRangeFrom, checkDateRangeTo } from '@/directives/common'
import MultiDeptHelpList from '@/components/core/common/dialog/MultiDeptHelpList.vue'
import MultiPolTypeHelpList from '@/components/core/common/dialog/MultiPolTypeHelpList.vue'
import SortOrderCustomHelpList from '@/components/core/common/dialog/SortOrderCustomHelpList.vue'

export default {
  data() {
    return {
      labels: {
        tranDateFrom: 'Transaction Date From*',
        tranDateTo: 'Transaction Date To*',
        dept: 'Department(s)',
        polType: 'Policy Type(s)',
        pbPrtParam: 'Print Runtime Parameters',
        format: 'Export Format',
        export: 'Export to'
      },
      reportForm: {
        tranDateFrom: undefined,
        tranDateTo: undefined,
        dept: 'All',
        polType: 'All',
        pageHeaderSort: '',
        detailLineSort: '',
        pbPrtParam: true,
        format: undefined
      },
      // HelpList
      multiDeptHelpList: false,
      multiPolTypeHelpList: false,
      // Other
      today: new Date(),
      menu: {},
      supportedFormat: [],
      loading: false,
      pageHeaderSortError: false,
      detailLineSortError: false,
      orderIdList: ['AG', 'BR', 'BC', 'CO', 'DR', 'LO', 'NY', 'PT', 'UY'],
      pageHeaderSorting: 'Page Header Sorting Order',
      detailLineSorting: 'Detail Line Sorting Order',
      selectedSize: 3,
      panel1: [0],
      panel2: [],
      panel3: [0]
    }
  },
  computed: {
    rptMenu() {
      return this.$store.state.reports.uwReports.menu
    },
    supportedList() {
      const result = []
      if (this.supportedFormat && this.supportedFormat.length > 0) {
        this.supportedFormat.map(format => {
          if (reportFormat[format]) {
            result.push(reportFormat[format])
          }
        })
      }
      return result
    }
  },
  methods: {
    async doInit() {
      try {
        if (this.$store.state.reports.uwReports.menu !== 1) {
          await this.$store.dispatch('uwReportsInit')
        }
        const apiURI = this.rptMenu.initInBusAnalysisGross
        const result = await this.$http.get(apiURI)
        if (result.data) {
          const { data } = result
          if (data) {
            this.supportedFormat = data.data.format
            this.menu = extractMenu(result.data._links)
          }
        }
      } catch (err) {
        throw err
      }
    },
    doAssign(type, obj) {
      switch (type) {
        case 'dept':
          this.reportForm.dept = []
          this.reportForm.dept = obj.finalSelectedItems.toString()
          break
        case 'polType':
          this.reportForm.polType = []
          this.reportForm.polType = obj.finalSelectedItems.toString()
          break
      }
    },
    doClose() {
      this.multiExecInChargeHelpList = false
      this.multiInterHelpList = false
      this.multiBusClsHelpList = false
      this.multiBusClsGrpHelpList = false
    },
    doExport() {
      let pageHeaderSort = []
      for (let i in this.$refs.pageHeaderSortRef.sortOrder) {
        pageHeaderSort.push(
          this.$refs.pageHeaderSortRef.phNewSortHtml[i].children[1].innerHTML +
            ':' +
            this.$refs.pageHeaderSortRef.phNewSortHtml[i].children[2].innerHTML
        )
      }
      this.reportForm.pageHeaderSort = pageHeaderSort.toString()
      let detailLineSort = []
      for (let i in this.$refs.detailLineSortRef.sortOrder) {
        detailLineSort.push(
          this.$refs.detailLineSortRef.phNewSortHtml[i].children[1].innerHTML +
            ':' +
            this.$refs.detailLineSortRef.phNewSortHtml[i].children[2].innerHTML
        )
      }
      this.reportForm.detailLineSort = detailLineSort.toString()
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          if (pageHeaderSort.length > 0) {
            this.pageHeaderSortError = false
          } else {
            this.pageHeaderSortError = true
          }
          if (detailLineSort.length > 0) {
            this.detailLineSortError = false
          } else {
            this.detailLineSortError = true
          }
          if (pageHeaderSort.length > 0 && detailLineSort.length > 0) {
            this.processExport()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async processExport() {
      const apiURI = this.menu.exportInBusAnalysisGross
      if (apiURI) {
        try {
          this.loading = true
          await this.$http.post(apiURI, this.reportForm)
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    fromDateValidation(toDate1) {
      if (toDate1) {
        return 'required|checkDateRangeFrom:' + toDate1
      } else {
        return 'required|checkDateRangeFrom'
      }
    },
    toDateValidation(fromDate1) {
      if (fromDate1) {
        return 'required|checkDateRangeTo:' + fromDate1
      } else {
        return 'required|checkDateRangeTo'
      }
    }
  },
  created() {
    this.doInit()
    this.$validator.extend('checkDateRangeFrom', checkDateRangeFrom)
    this.$validator.extend('checkDateRangeTo', checkDateRangeTo)
  },
  watch: {},
  mixins: [MessageMixin, commonAjaxMixin],
  components: {
    MultiDeptHelpList,
    MultiPolTypeHelpList,
    SortOrderCustomHelpList
  }
}
</script>
